import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
  colors,
  lighten,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useAppStore } from "@/global/store";
import {
  selectCartEvents,
  selectIsAnyPerkItemSelected,
} from "@/global/store/data/purchaseSeats/cartManager";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { PerkItemCard } from "./PerkItemCard";
import { PerkParentType } from "./perks.types";
import { getPerkImageSliderBtnText } from "../utils.perks";

export const GroupedPerkSection = (props: {
  title: string;
  description?: string;
  perkParents: PerkParentType[];
  isOpenByDefault?: boolean;
  singleSelect?: boolean;
  onClickImageSliderButton: (perkParent: PerkParentType) => void;
}) => {
  const theme = useTheme();
  const teamConfig = useTeamConfig();
  const [isOpen, setIsOpen] = React.useState(props?.isOpenByDefault ?? true);

  const allPerkItems = props.perkParents.flatMap((parent) => parent.perks);
  const isAnyPerkItemSelected = useAppStore(
    selectIsAnyPerkItemSelected(allPerkItems)
  );

  // ids of this section's perks that are currently in the cart
  const cartEvents = useAppStore(selectCartEvents);
  const sectionPerkIds = new Set(allPerkItems.map((itm) => itm.id));
  const selectedSectionPerkIds = new Set(
    cartEvents
      .flatMap((cartEvent) => cartEvent.perks || [])
      .map((perk) => perk.id)
      .filter((id) => sectionPerkIds.has(id))
  );

  React.useEffect(() => {
    if (isAnyPerkItemSelected) setIsOpen(true);
  }, [isAnyPerkItemSelected]);

  return (
    <Accordion
      expanded={isOpen}
      sx={{
        borderRadius: "6 !important",
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        paddingTop: 1,
        paddingBottom: 1,
        cursor: "default",
        transition: "all 0.15s ease-in-out",
        "&:hover": {
          boxShadow: `0px 0px 0px 3px ${lighten(
            theme.palette.primary.main,
            0.7
          )}`,
        },
      }}
    >
      <AccordionSummary
        aria-controls="grouped-perks-content"
        id={`grouped-perk-section-${props.title}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Box width={"100%"}>
          <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
            <Box flexGrow={1}>
              <Typography
                fontWeight={"600"}
                variant="body1"
                lineHeight={1.1}
                color={"text.primary"}
              >
                {props.title}
              </Typography>
              {props.description ? (
                <Typography
                  mt={0.5}
                  variant="body2"
                  color={"text.secondary"}
                  lineHeight={1.1}
                >
                  {props.description}
                </Typography>
              ) : null}
            </Box>
            <Box>
              <IconButton
                size="medium"
                onClick={() => setIsOpen(!isOpen)}
                sx={{
                  color: colors.grey[800],
                  backgroundColor: `${colors.grey[100]} !important`,
                  transform: `rotate(${isOpen ? 180 : 0}deg)`,
                  transition: `transform 0.15s ease-in-out`,
                  transformOrigin: "center center",
                  "&:active": { backgroundColor: colors.grey[200] },
                }}
              >
                <KeyboardArrowDownRoundedIcon />
              </IconButton>
            </Box>
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {props.perkParents.map((perkParent) => {
            const sliderImages =
              teamConfig?.uiConfigs?.perks?.images?.[perkParent.perkCode] || [];
            return (
              <Grid key={perkParent.perkCode} item xs={12}>
                <Stack spacing={1}>
                  {sliderImages.length ? (
                    <Stack direction={"row"} justifyContent={"flex-end"}>
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          fontSize: "0.8rem",
                          lineHeight: "1.1",
                          fontWeight: 600,
                          borderRadius: "32px",
                          paddingLeft: "12px",
                          paddingRight: "12px",
                        }}
                        onClick={() =>
                          props.onClickImageSliderButton(perkParent)
                        }
                      >
                        {getPerkImageSliderBtnText(
                          perkParent.perkCode,
                          teamConfig
                        )}
                      </Button>
                    </Stack>
                  ) : null}
                  {perkParent.perks.map((perkItem) => (
                    <PerkItemCard
                      key={perkItem.id}
                      perkParent={perkParent}
                      perkItem={perkItem}
                      hideParentIcon={true}
                      hideEventDetails={true}
                      hideTitleDesc={false}
                      isDisabled={
                        props?.singleSelect &&
                        selectedSectionPerkIds.size > 0 &&
                        !selectedSectionPerkIds.has(perkItem.id)
                      }
                    />
                  ))}
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
